import {
  Timestamp,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firebase } from '../lib/firebase'
import { blockRef, blocksRef } from './paths'
import type { Block, BlockDoc } from './types'
import type { BlockWrite, Plan } from './blockPlan'

export function toBlock(snapshot: QueryDocumentSnapshot<BlockDoc>): Block {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    start: data.start.toMillis(),
    end: data.end ? data.end.toMillis() : null,
    categoryId: data.categoryId,
    note: data.note ?? null,
    tags: data.tags ?? [],
    source: data.source,
  }
}

/**
 * Commits a plan as one atomic batch.
 *
 * Deliberately a batch and not a transaction: transactions need a server
 * round-trip and would fail with no signal, and switching category offline is a
 * hard requirement. A batch is applied locally first, queued, and lands
 * all-or-nothing — so a dropped connection can never leave two open blocks.
 */
export async function commitPlan(uid: string, plan: Plan): Promise<void> {
  if (!plan.ok) throw new Error(plan.message)
  if (plan.writes.length === 0) return
  const batch = writeBatch(firebase().db)
  for (const write of plan.writes) applyWrite(batch, uid, write)
  // Intentionally not awaited beyond the local commit: Firestore resolves this
  // once the write is durable on the server, which may be hours later offline.
  void batch.commit()
}

function applyWrite(batch: ReturnType<typeof writeBatch>, uid: string, write: BlockWrite): void {
  if (write.kind === 'create') {
    const { id, start, end, ...rest } = write.block
    batch.set(blockRef(uid, id), {
      ...rest,
      start: Timestamp.fromMillis(start),
      end: end === null ? null : Timestamp.fromMillis(end),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as BlockDoc)
    return
  }
  if (write.kind === 'delete') {
    batch.delete(blockRef(uid, write.id))
    return
  }
  const patch: Record<string, unknown> = { updatedAt: serverTimestamp() }
  for (const [key, value] of Object.entries(write.patch)) {
    if (key === 'start' || key === 'end') {
      patch[key] = value === null || value === undefined ? null : Timestamp.fromMillis(value as number)
    } else {
      patch[key] = value
    }
  }
  batch.update(blockRef(uid, write.id), patch)
}

/** The running block, if any. Live, and served from cache while offline. */
export function subscribeOpenBlock(
  uid: string,
  onChange: (block: Block | null) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(blocksRef(uid), where('end', '==', null), orderBy('start', 'desc'), limit(1))
  return onSnapshot(
    q,
    (snapshot) => onChange(snapshot.docs[0] ? toBlock(snapshot.docs[0]) : null),
    (error) => onError?.(error),
  )
}

/** Every block overlapping [from, to) — the query behind Today and Week. */
export function subscribeBlocksInRange(
  uid: string,
  from: Date,
  to: Date,
  onChange: (blocks: Block[]) => void,
  onError?: (error: Error) => void,
  /** Insights reads months at a time; a day needs far fewer. */
  cap = 2000,
): () => void {
  // A block that started before the window can still overlap it, so the window is
  // widened backwards and filtered client-side rather than needing a second query.
  const q = query(
    blocksRef(uid),
    where('start', '<', Timestamp.fromDate(to)),
    orderBy('start', 'desc'),
    limit(cap),
  )
  return onSnapshot(
    q,
    (snapshot) => {
      const blocks = snapshot.docs
        .map(toBlock)
        .filter((block) => (block.end ?? Number.POSITIVE_INFINITY) > from.getTime())
        .sort((a, b) => a.start - b.start)
      onChange(blocks)
    },
    (error) => onError?.(error),
  )
}
